
import { defineComponent } from 'vue';
import { PostDto, SeriesDto, SimpleCommentDto } from '@/api/models/blog.dtos';
import {
  createComment,
  createGuestComment,
  deletePost,
  getBlogSeriesDetail,
  getPost,
  isLikePost,
  likePost,
  unlikePost
} from '@/api/blog';
import { HttpApiError } from '@/api/common/httpApiClient';
import AccountProfileImageButton from '@/components/accounts/AccountProfileImageButton.vue';
import store from '@/store';
import SimpleCommentItem from '@/components/comments/SimpleCommentItem.vue';
import PostViewer from '@/components/posts/PostViewer.vue';

export default defineComponent({
  name: 'PostView',
  components: { PostViewer, SimpleCommentItem, AccountProfileImageButton },
  props: {
    blogId: {
      type: String,
      required: true,
    },
    postUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      post: {} as PostDto,
      comments: {} as Array<SimpleCommentDto>,
      tags: {} as Array<string>,
      likeCount: 0,
      isLike: false,
      newComment: '',
      guestCommentName: '',
      guestCommentPassword: '',
      series: {} as SeriesDto | null,
    };
  },
  computed: {
    longCreateAt() {
      if (!this.post?.createdAt) {
        return null;
      }
      const date = new Date(this.post.createdAt);
      const hours = `${date.getHours()}`.padStart(2, '0');
      const minutes = `${date.getMinutes()}`.padStart(2, '0');
      return `${date.getFullYear()}. ${date.getMonth() + 1}. ${date.getDate()}. ${hours}:${minutes}`;
    },
    longLastModifiedAt() {
      if (!this.post?.lastModifiedAt) {
        return null;
      }
      const date = new Date(this.post.lastModifiedAt);
      const hours = `${date.getHours()}`.padStart(2, '0');
      const minutes = `${date.getMinutes()}`.padStart(2, '0');
      return `${date.getFullYear()}. ${date.getMonth() + 1}. ${date.getDate()}. ${hours}:${minutes}`;
    },
    isMyPost() {
      return this.post?.author?.blogId === store.state.accountStore.blogId;
    },
    isLoggedIn(): boolean {
      return store.getters['accountStore/isLoggedIn'] ?? false;
    },
    isValidNewComment() {
      return this.newComment.length > 0 && (this.isLoggedIn || (this.guestCommentName.length > 0 && this.guestCommentPassword.length > 0));
    },
    allCommentCount() {
      return this.comments?.reduce?.((previous, current) => previous + 1 + current.childrenCount, 0) ?? 0;
    },
  },
  watch: {
    postUrl() {
      this.loadPost();
    },
  },
  methods: {
    async loadPost() {
      if (!this.blogId || !this.postUrl)
        return;

      await getPost(this.blogId, this.postUrl)
      .then(async (post) => {
        this.post = post;
        this.likeCount = post.likeCount;
        this.comments = post.comments;
        this.tags = post.tags?.map(tag => tag.name) ?? [];
        document.title = this.post.title;

        if (post.seriesUrl) {
          this.series = await getBlogSeriesDetail(this.blogId, post.seriesUrl);
        } else {
          this.series = null;
        }
      })
      .catch((error: HttpApiError) => {
        alert(error.getErrorMessage());
        this.$router.push(`/`);
      });

      if (this.isLoggedIn) {
        await isLikePost(this.blogId, this.postUrl)
        .then((isLike) => {
          this.isLike = isLike;
        })
        .catch((error: HttpApiError) => {
          alert(error.getErrorMessage());
          this.$router.push(`/`);
        });
      }
    },
    async writeComment() {
      if (this.isLoggedIn) {
        await createComment(this.blogId, this.postUrl, {
          content: this.newComment,
        })
        .then((comment) => {
          this.newComment = '';
          this.comments.push(Object.assign(comment, {
            childrenCount: comment.children.length,
          }));
        })
        .catch((error: HttpApiError) => {
          alert(error.getErrorMessage());
        });
      } else {
        await createGuestComment(this.blogId, this.postUrl, {
          content: this.newComment,
          name: this.guestCommentName,
          password: this.guestCommentPassword
        })
        .then((comment) => {
          this.newComment = '';
          this.guestCommentPassword = '';
          this.comments.push(Object.assign(comment, {
            childrenCount: comment.children.length,
          }));
        })
        .catch((error: HttpApiError) => {
          alert(error.getErrorMessage());
        });
      }
    },
    commentDeleted(commentId: string) {
      this.comments = this.comments.filter((x) => x.id !== commentId);
    },
    commentChanged(comment: SimpleCommentDto) {
      this.comments.splice(this.comments.findIndex(x => x.id === comment.id), 1, comment);
    },
    async likePost() {
      await likePost(this.blogId, this.postUrl)
      .then(async () => {
        this.likeCount++;
        this.isLike = true;
      })
      .catch((error: HttpApiError) => {
        alert(error.getErrorMessage());
      });
    },
    async unlikePost() {
      await unlikePost(this.blogId, this.postUrl)
      .then(async () => {
        this.likeCount--;
        this.isLike = false;
      })
      .catch((error: HttpApiError) => {
        alert(error.getErrorMessage());
      });
    },
    async modifyPost() {
      this.$router.push(`/write?post=${this.postUrl}`);
    },
    async deletePost() {
      if (confirm('게시글을 삭제하시겠습니까?') != true) {
        return;
      }

      await deletePost(this.postUrl)
      .then(() => {
        alert('게시글을 삭제했습니다.');
        this.$router.push(`/`);
      })
      .catch((error: HttpApiError) => {
        alert(error.getErrorMessage());
      });
    },
  },
  created() {
    this.loadPost();
  },
});
